import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Collapse,
  Paper,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { getTournamentTeams } from '../../../services/tournament.service';

const getPodiumColor = (idx) => {
  if (idx === 0) return '#fbbf24';
  if (idx === 1) return '#cbd5e1';
  if (idx === 2) return '#d97706';
  return '#94a3b8';
};

const headerCellSx = {
  py: 0.75,
  background: '#0f172a',
  color: '#94a3b8',
  fontWeight: 'bold',
  fontSize: '0.78rem',
  borderColor: 'rgba(255, 255, 255, 0.08)',
  letterSpacing: 0.5,
};

const TeamRow = ({ team, index }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen(!open)}
        style={{ cursor: 'pointer' }}
        sx={{ '&:hover': { background: 'rgba(255, 255, 255, 0.02) !important' } }}
      >
        <TableCell sx={{ py: 0.75, borderColor: 'rgba(255,255,255,0.05)', width: 50, textAlign: 'center', color: '#64748b', padding: '4px' }}>
          {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
        </TableCell>
        <TableCell sx={{ py: 0.75, borderColor: 'rgba(255,255,255,0.05)', fontWeight: 'bold', color: getPodiumColor(index), width: 70, textAlign: 'center' }}>
          {team.posicion ?? index + 1}
        </TableCell>
        <TableCell sx={{ py: 0.75, borderColor: 'rgba(255,255,255,0.05)', color: '#cbd5e1', fontWeight: 500 }}>
          {team.nombre}
        </TableCell>
        <TableCell sx={{ py: 0.75, borderColor: 'rgba(255,255,255,0.05)', color: '#10b981', fontWeight: 'bold', width: 100, textAlign: 'center' }}>
          {team.puntos ?? '—'}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0, background: '#080e1a' }} colSpan={4}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, padding: 2, background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255,255,255,0.08)' }}>
              {team.jugadores && team.jugadores.length > 0 ? (
                <Table size="small" sx={{ width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headerCellSx, width: 40 }}>#</TableCell>
                      <TableCell sx={headerCellSx}>Jugador</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {team.jugadores.map((player, idx) => (
                      <TableRow key={idx} sx={{ '&:hover': { background: 'rgba(255, 255, 255, 0.02) !important' } }}>
                        <TableCell sx={{ py: 0.5, color: '#64748b', borderColor: 'rgba(255,255,255,0.05)', fontSize: '0.8rem' }}>
                          {idx + 1}
                        </TableCell>
                        <TableCell sx={{ py: 0.5, color: '#cbd5e1', borderColor: 'rgba(255,255,255,0.05)', fontSize: '0.8rem' }}>
                          {player.apellido_nombre}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                  No hay jugadores registrados en este equipo.
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const TournamentTeams = ({ torId }) => {
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const fetchTeams = async () => {
      setIsLoading(true);
      try {
        const data = await getTournamentTeams(torId);
        setTeams(data);
      } catch (e) {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTeams();
  }, [torId]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: '#7c8cf8' }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography color="error" sx={{ py: 3 }}>
        Error al cargar los equipos.
      </Typography>
    );
  }

  if (!teams.length) {
    return (
      <Typography sx={{ color: '#9aa5b4', py: 3 }}>
        No hay equipos inscriptos en este torneo.
      </Typography>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: 0,
        border: '1px solid rgba(255,255,255,0.08)',
        background: 'transparent',
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerCellSx, width: 50, textAlign: 'center' }}></TableCell>
            <TableCell sx={{ ...headerCellSx, width: 70, textAlign: 'center' }}>Pos.</TableCell>
            <TableCell sx={headerCellSx}>Nombre del Equipo</TableCell>
            <TableCell sx={{ ...headerCellSx, width: 100, textAlign: 'center', color: '#10b981' }}>Puntos</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {teams.map((t, idx) => (
            <TeamRow key={idx} team={t} index={idx} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TournamentTeams;
